/* ==========================================================================
   Quas sauna hub: two small map behaviours, no dependencies.

   <quas-sauna-atlas>
     The tradition map follows the reading: the entry nearest the middle of
     the viewport lights its pin. Selecting a pin scrolls to its entry.
     Pins are ordinary links to the entries, so everything works without
     this script.

   <quas-sauna-venue-map>
     Venue pins and list rows highlight each other on hover and focus.
     Selecting a pin moves focus to the venue in the list.

   Reduced motion: scrolling jumps instead of gliding.
   ========================================================================== */

(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollToEntry = (el) => {
    el.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
  };

  class QuasSaunaAtlas extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.pins = new Map([...this.querySelectorAll('[data-atlas-pin]')].map((pin) => [pin.dataset.atlasPin, pin]));
      this.entries = [...this.querySelectorAll('[data-atlas-entry]')];
      if (!this.entries.length) return;

      this.pins.forEach((pin, key) => {
        pin.addEventListener('click', (event) => {
          const entry = this.entries.find((item) => item.dataset.atlasEntry === key);
          if (!entry) return;
          event.preventDefault();
          this.setCurrent(key);
          this.lockUntil = Date.now() + (reduced() ? 0 : 900);
          scrollToEntry(entry);
          const focusTarget = entry.querySelector('summary') || entry;
          focusTarget.focus({ preventScroll: true });
        });
      });

      this.entries.forEach((entry) => {
        const key = entry.dataset.atlasEntry;
        entry.addEventListener('pointerenter', () => this.setHover(key, true));
        entry.addEventListener('pointerleave', () => this.setHover(key, false));
        entry.addEventListener('focusin', () => this.setCurrent(key));
      });

      if ('IntersectionObserver' in window) {
        const visible = new Set();
        const pick = () => {
          if (this.lockUntil && Date.now() < this.lockUntil) return;
          const middle = window.innerHeight / 2;
          let best = null;
          let bestDistance = Infinity;
          visible.forEach((entry) => {
            const box = entry.getBoundingClientRect();
            const distance = Math.abs(box.top + box.height / 2 - middle);
            if (distance < bestDistance) {
              best = entry;
              bestDistance = distance;
            }
          });
          if (best) this.setCurrent(best.dataset.atlasEntry);
        };
        this.observer = new IntersectionObserver(
          (records) => {
            records.forEach((record) => (record.isIntersecting ? visible.add(record.target) : visible.delete(record.target)));
            pick();
          },
          { rootMargin: '-30% 0px -30% 0px' }
        );
        this.entries.forEach((entry) => this.observer.observe(entry));
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            pick();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.setCurrent(this.entries[0].dataset.atlasEntry);
      this.classList.add('is-interactive');
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    setCurrent(key) {
      if (this.current === key) return;
      this.current = key;
      this.pins.forEach((pin, pinKey) => pin.classList.toggle('is-current', pinKey === key));
      this.entries.forEach((entry) => entry.classList.toggle('is-current', entry.dataset.atlasEntry === key));
    }

    setHover(key, on) {
      this.pins.get(key)?.classList.toggle('is-hover', on);
    }
  }

  class QuasSaunaVenueMap extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const pins = [...this.querySelectorAll('[data-venue-pin]')];
      const items = [...this.querySelectorAll('[data-venue-item]')];
      const set = (key, on) => {
        pins.forEach((pin) => pin.dataset.venuePin === key && pin.classList.toggle('is-active', on));
        items.forEach((item) => item.dataset.venueItem === key && item.classList.toggle('is-active', on));
      };
      [...pins, ...items].forEach((el) => {
        const key = el.dataset.venuePin || el.dataset.venueItem;
        el.addEventListener('pointerenter', () => set(key, true));
        el.addEventListener('pointerleave', () => set(key, false));
        el.addEventListener('focusin', () => set(key, true));
        el.addEventListener('focusout', () => set(key, false));
      });
      pins.forEach((pin) => {
        pin.addEventListener('click', (event) => {
          const item = items.find((row) => row.dataset.venueItem === pin.dataset.venuePin);
          if (!item) return;
          event.preventDefault();
          items.forEach((row) => row.classList.remove('is-picked'));
          item.classList.add('is-picked');
          scrollToEntry(item);
          (item.querySelector('a') || item).focus({ preventScroll: true });
        });
      });
      this.classList.add('is-interactive');
    }
  }

  if (!customElements.get('quas-sauna-atlas')) customElements.define('quas-sauna-atlas', QuasSaunaAtlas);
  if (!customElements.get('quas-sauna-venue-map')) customElements.define('quas-sauna-venue-map', QuasSaunaVenueMap);
})();
